import React, { useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCurrentUserDetails } from 'entities/user/model';
import { useOrgIntegrations } from '../../features/organization/integrations/useOrgIntegrations';

import { Synaps } from '@synaps-io/verify-sdk';
import { useQuickBooksAuthorise } from '../../features/quickBooksOnline/useQuickBooksAuthorise';
import { Button } from '../../shared/ui/Button/Button';

const useEasyPayKyc = () => {
  const { getAccessTokenSilently: getToken } = useAuth0();
  const { hasEasyPayIntegration, getCurrentOrganizationId } =
    useCurrentUserDetails();
  const kycQuery = useQuery({
    queryKey: ['monerium-balances'],
    queryFn: async () => {
      const token = await getToken();

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/public/easypay/kyc`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              organizationId: getCurrentOrganizationId(),
            },
          }
        );
        return response.data?.result;
      } catch (error) {
        console.log(error);
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    enabled: hasEasyPayIntegration() && !!getCurrentOrganizationId(),
  });

  return {
    kyc: kycQuery.data,
  };
};

const OrgIntegrations = () => {
  const { kyc } = useEasyPayKyc();
  const { getQBOIntegration } = useOrgIntegrations();
  const {
    authUri: quickBooksAuthUri,
    isAuthorisationPending,
    authorise,
  } = useQuickBooksAuthorise();

  useEffect(() => {
    let ignore = false;

    if (!ignore && kyc?.ongoingSessionId) {
      Synaps.init({
        sessionId: kyc?.ongoingSessionId,
        service: 'corporate',
        onFinish: () => {
          // hide button, discard kyc state
        },
        mode: 'modal',
      });
    }

    return () => {
      ignore = true;
    };
  }, [kyc?.ongoingSessionId]);

  useEffect(() => {
    if (quickBooksAuthUri) {
      window.location.href = quickBooksAuthUri;
    }
  }, [quickBooksAuthUri]);

  // const renderEasyPayIntegration = () => {
  //   if (kyc && kyc.ongoingSessionId) {
  //     return (
  //       <div className="mt-10">
  //         <div className="bg-white  shadow dark:bg-slate-800 sm:rounded-lg sm:p-6">
  //           <div className="md:grid md:grid-cols-3 md:gap-6">
  //             <div className="md:col-span-1">
  //               <h3 className="text-lg font-medium leading-6 text-white ">
  //                 Crypto to Fiat Integration
  //               </h3>
  //               <p className="mt-1 text-sm text-gray-500 dark:text-gray-200">
  //                 Finish KYC process in order to start using offramp
  //               </p>
  //             </div>
  //             <div className="mt-5 sm:overflow-hidden sm:rounded-md md:col-span-2 md:mt-0">
  //               <Button
  //                 size="sm"
  //                 color="indigo"
  //                 isProcessing={isAuthorisationPending}
  //                 disabled={isAuthorisationPending}
  //                 onClick={() => {
  //                   Synaps.show();
  //                 }}>
  //                 Initiate KYC session
  //               </Button>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     );
  //   }

  //   return null;
  // };

  const renderQuickBooksIntegration = () => {
    return (
      <div className="bg-white  shadow dark:bg-slate-800 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-white ">
              Quick Books Online Integration
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-200">
              Connect to your QuickBooks or Xero to easily sync your bills and
              invoices.
            </p>
          </div>
          <div className="mt-5 flex items-center justify-end sm:rounded-md md:col-span-2 md:mt-0">
            {getQBOIntegration() ? (
              <span className="text-slate-300">Connected</span>
            ) : (
              <Button
                size="sm"
                color="indigo"
                isProcessing={isAuthorisationPending || !!quickBooksAuthUri}
                disabled={isAuthorisationPending || !!quickBooksAuthUri}
                onClick={() => {
                  authorise();
                }}>
                Connect Quick Books Online
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return <div className="">{renderQuickBooksIntegration()}</div>;
};

export default OrgIntegrations;
